import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { CalendarClock, ClipboardList, Copy, MessageSquarePlus, RefreshCw, Search, UserRoundCheck } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import StoreLayout from "./StoreLayout";
import { copyPhoneNumber } from "./phoneActions";
import { preferredStoreLocationId, rememberedStoreLocationId } from "./storeLocationPreference";
import {
  StoreApiError,
  StorePaginationMeta,
  StoreReservation,
  StoreReservationInteraction,
  StoreReservationStatus,
  StoreUser,
  addStoreReservationNote,
  getCurrentStoreUser,
  getStoreReservation,
  listStoreReservations,
  logoutStore,
  updateStoreReservationStatus,
} from "./api";

const statusOptions: { value: StoreReservationStatus | ""; label: string }[] = [
  { value: "", label: "全部狀態" },
  { value: "New", label: "新預約" },
  { value: "Contacted", label: "已聯絡" },
  { value: "Booked", label: "已預約" },
  { value: "Confirmed", label: "已確認" },
  { value: "Arrived", label: "已到店" },
  { value: "Completed", label: "已完成" },
  { value: "FollowUp", label: "需回訪" },
  { value: "NoShow", label: "未到" },
  { value: "Cancelled", label: "已取消" },
  { value: "Closed", label: "已結案" },
];

const statusLabels = Object.fromEntries(statusOptions.filter((item) => item.value).map((item) => [item.value, item.label]));

const statusClass: Record<string, string> = {
  New: "bg-[#eef3f8] text-[#255071]",
  Contacted: "bg-[#e9f4ec] text-[#245438]",
  Booked: "bg-[#e4f0e8] text-[#203d32]",
  Confirmed: "bg-[#e4f0e8] text-[#203d32]",
  Arrived: "bg-[#fff6df] text-[#7a5610]",
  Completed: "bg-[#eeeeee] text-[#59635d]",
  FollowUp: "bg-[#f3ecdf] text-[#8a6a2f]",
  NoShow: "bg-[#fdebea] text-[#9a2d2d]",
  Cancelled: "bg-[#fdebea] text-[#9a2d2d]",
  Closed: "bg-[#eeeeee] text-[#59635d]",
};

const defaultPagination: StorePaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

function currentYearStart() {
  return `${new Date().getFullYear()}-01-01`;
}

function currentYearEnd() {
  return `${new Date().getFullYear()}-12-31`;
}

function formatDateTime(date?: string | null, time?: string | null) {
  if (!date && !time) return "未指定時間";
  if (!date) return time || "未指定時間";
  return `${date}${time ? ` ${time}` : ""}`;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${statusClass[status] || "bg-[#eeeeee] text-[#59635d]"}`}>
      {statusLabels[status] || status}
    </span>
  );
}

function ReservationCard({ item, selected }: { item: StoreReservation; selected: boolean }) {
  return (
    <Link
      className={`block rounded-md border bg-white p-4 transition hover:border-[#b8c8bf] hover:shadow-[0_8px_22px_rgba(32,61,50,.08)] ${
        selected ? "border-[#9ac8aa] ring-2 ring-[#9ac8aa]/20" : "border-[#dfe6e1]"
      }`}
      to={`/store/reservations/${encodeURIComponent(item.SourceType)}/${encodeURIComponent(item.SourceId)}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-base font-extrabold text-[#203d32]">{item.CustomerName}</p>
          <p className="mt-1 text-sm font-medium text-[#59665f]">{formatDateTime(item.AppointmentDate, item.AppointmentTime)}</p>
        </div>
        <StatusBadge status={item.Status} />
      </div>
      <div className="mt-3 grid gap-1 text-sm text-[#59665f]">
        <span>{item.Title}</span>
        <span>{item.ProductName || item.LocationName}</span>
      </div>
    </Link>
  );
}

function InfoTile({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] px-3 py-2">
      <p className="text-xs font-bold text-[#7b877f]">{label}</p>
      <div className="mt-1 text-sm font-bold leading-6 text-[#203d32]">{value || "-"}</div>
    </div>
  );
}

export default function StoreReservations() {
  const navigate = useNavigate();
  const { sourceType, id } = useParams();
  const [user, setUser] = useState<StoreUser | null>(null);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(() => rememberedStoreLocationId());
  const [items, setItems] = useState<StoreReservation[]>([]);
  const [reservation, setReservation] = useState<StoreReservation | null>(null);
  const [interactions, setInteractions] = useState<StoreReservationInteraction[]>([]);
  const [pagination, setPagination] = useState<StorePaginationMeta>(defaultPagination);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [status, setStatus] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [keyword, setKeyword] = useState("");
  const [dateFrom, setDateFrom] = useState(currentYearStart());
  const [dateTo, setDateTo] = useState(currentYearEnd());
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [nextFollowUpAt, setNextFollowUpAt] = useState("");
  const [saving, setSaving] = useState(false);

  const effectiveLocationId = useMemo(() => (
    preferredStoreLocationId(user, currentLocationId)
  ), [currentLocationId, user]);

  async function loadList(locationId?: string | null, page = pagination.Page) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentStoreUser();
      setUser(me.User);
      const nextLocationId = preferredStoreLocationId(me.User, locationId || currentLocationId);
      setCurrentLocationId(nextLocationId);
      const data = await listStoreReservations({ LocationId: nextLocationId, Status: status, SourceType: sourceFilter, Keyword: keyword.trim(), DateFrom: dateFrom, DateTo: dateTo, Page: page, PerPage: pagination.PerPage });
      setItems(data.Reservations);
      setCounts(data.Counts || {});
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "預約列表載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail() {
    if (!sourceType || !id) {
      setReservation(null);
      setInteractions([]);
      return;
    }
    setDetailLoading(true);
    setMessage("");
    try {
      const data = await getStoreReservation(sourceType, id);
      if (effectiveLocationId && data.Reservation.LocationId !== effectiveLocationId) {
        setReservation(null);
        setInteractions([]);
        setMessage("此預約不屬於目前選取門市，已回到預約列表。");
        navigate("/store/reservations", { replace: true });
        return;
      }
      setReservation(data.Reservation);
      setInteractions(data.Interactions || []);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "預約詳情載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    loadList(null, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, id, effectiveLocationId]);

  async function handleLogout() {
    await logoutStore().catch(() => undefined);
    navigate("/store-login", { replace: true });
  }

  function handleLocationChange(locationId: string) {
    setCurrentLocationId(locationId);
    setReservation(null);
    setInteractions([]);
    setNoteBody("");
    setNextFollowUpAt("");
    navigate("/store/reservations");
    loadList(locationId, 1);
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    loadList(effectiveLocationId, 1);
  }

  async function handleStatusUpdate(nextStatus: StoreReservationStatus) {
    if (!reservation) return;
    setSaving(true);
    setMessage("");
    try {
      const data = await updateStoreReservationStatus(reservation.SourceType, reservation.SourceId, nextStatus);
      setReservation(data.Reservation);
      await loadList(effectiveLocationId, pagination.Page);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "狀態更新失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddNote(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reservation) return;
    setSaving(true);
    setMessage("");
    try {
      const data = await addStoreReservationNote(reservation.SourceType, reservation.SourceId, { Body: noteBody, NextFollowUpAt: nextFollowUpAt });
      if (data.Reservation) setReservation(data.Reservation);
      setNoteBody("");
      setNextFollowUpAt("");
      await loadDetail();
      await loadList(effectiveLocationId, pagination.Page);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "備註新增失敗。");
    } finally {
      setSaving(false);
    }
  }

  const totalOpen = (counts.New || 0) + (counts.Contacted || 0) + (counts.Booked || 0) + (counts.Confirmed || 0) + (counts.FollowUp || 0);

  return (
    <StoreLayout currentLocationId={effectiveLocationId} onLocationChange={handleLocationChange} onLogout={handleLogout} title="預約管理" user={user}>
      <div className="space-y-4">
        <form className="grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 sm:grid-cols-2 2xl:grid-cols-[minmax(150px,170px)_minmax(150px,170px)_minmax(300px,1fr)_minmax(180px,200px)_minmax(180px,200px)_auto_auto] 2xl:items-end" onSubmit={handleSearch}>
          <div className="sm:col-span-2 2xl:col-span-1">
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="store-reservation-status">狀態</label>
            <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-reservation-status" value={status} onChange={(event) => setStatus(event.target.value)}>
              {statusOptions.map((item) => <option key={item.value || "all"} value={item.value}>{item.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="store-reservation-source">來源</label>
            <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-reservation-source" value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}>
              <option value="">全部來源</option>
              <option value="FathersDay">活動預約</option>
              <option value="ProductTrial">預約試戴</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="store-reservation-keyword">關鍵字</label>
            <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-reservation-keyword" placeholder="姓名、電話、門市" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="store-reservation-from">起日</label>
            <input className="h-11 w-full min-w-[180px] rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-reservation-from" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="store-reservation-to">迄日</label>
            <input className="h-11 w-full min-w-[180px] rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-reservation-to" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </div>
          <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white" type="submit">
            <Search size={18} />
            查詢
          </button>
          <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => loadList(effectiveLocationId, pagination.Page)} type="button">
            <RefreshCw size={18} />
            重新整理
          </button>
        </form>

        {message && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

        <div className="grid gap-3 sm:grid-cols-3">
          <InfoTile label="待處理" value={`${totalOpen} 筆`} />
          <InfoTile label="新預約" value={`${counts.New || 0} 筆`} />
          <InfoTile label="需回訪" value={`${counts.FollowUp || 0} 筆`} />
        </div>

        <div className="grid gap-4 2xl:grid-cols-[minmax(360px,480px)_1fr]">
          <section className="rounded-md border border-[#dfe6e1] bg-white p-3">
            <div className="mb-3 flex items-center justify-between gap-3 px-1">
              <div>
                <h2 className="font-extrabold text-[#203d32]">預約列表</h2>
                <p className="mt-1 text-sm text-[#7b877f]">共 {pagination.Total} 筆</p>
              </div>
              <ClipboardList className="text-[#315746]" size={20} />
            </div>
            {loading ? (
              <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-10 text-center text-sm text-[#7b877f]">載入中...</div>
            ) : items.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-10 text-center text-sm text-[#7b877f]">目前沒有符合條件的預約。</div>
            ) : (
              <div className="grid gap-3">
                {items.map((item) => (
                  <ReservationCard item={item} key={`${item.SourceType}-${item.SourceId}`} selected={item.SourceType === sourceType && item.SourceId === id} />
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-col gap-2 border-t border-[#edf0ee] pt-3 sm:flex-row sm:items-center sm:justify-between">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={pagination.Page <= 1 || loading} onClick={() => loadList(effectiveLocationId, pagination.Page - 1)} type="button">上一頁</button>
              <span className="text-center text-sm font-bold text-[#59665f]">{pagination.Page} / {pagination.TotalPages}</span>
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={pagination.Page >= pagination.TotalPages || loading} onClick={() => loadList(effectiveLocationId, pagination.Page + 1)} type="button">下一頁</button>
            </div>
          </section>

          <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
            {!sourceType || !id ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 text-center">
                <CalendarClock className="mb-3 text-[#315746]" size={34} />
                <h2 className="text-xl font-extrabold text-[#203d32]">選擇一筆預約查看詳情</h2>
                <p className="mt-2 max-w-[420px] text-sm leading-6 text-[#7b877f]">可在詳情中查看電話、更新狀態、留下聯絡備註與建立下次回訪。</p>
              </div>
            ) : detailLoading ? (
              <div className="min-h-[420px] rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-10 text-center text-sm text-[#7b877f]">詳情載入中...</div>
            ) : reservation ? (
              <div className="space-y-4">
                <div className="flex flex-col gap-3 border-b border-[#edf0ee] pb-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <StatusBadge status={reservation.Status} />
                      <span className="rounded-full bg-[#f4f6f5] px-2.5 py-1 text-xs font-bold text-[#59665f]">{reservation.Title}</span>
                    </div>
                    <h2 className="text-2xl font-extrabold text-[#203d32]">{reservation.CustomerName}</h2>
                    <p className="mt-1 text-sm text-[#59665f]">{formatDateTime(reservation.AppointmentDate, reservation.AppointmentTime)}</p>
                  </div>
                  <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white" onClick={() => copyPhoneNumber(reservation.CustomerPhone)} type="button">
                    <Copy size={18} />
                    複製電話
                  </button>
                </div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <InfoTile label="電話" value={reservation.CustomerPhone} />
                  <InfoTile label="門市" value={reservation.LocationName} />
                  <InfoTile label="產品 / 來源" value={reservation.ProductName || reservation.Title} />
                  <InfoTile label="最後聯絡" value={reservation.LastContactedAt || "-"} />
                  <InfoTile label="下次回訪" value={reservation.NextFollowUpAt || "-"} />
                  <InfoTile label="建立時間" value={reservation.CreatedAt || "-"} />
                </div>

                {reservation.Note && <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4 text-sm leading-6 text-[#59665f]">{reservation.Note}</div>}

                <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
                  <div className="mb-3 flex items-center gap-2 font-extrabold text-[#203d32]">
                    <UserRoundCheck size={18} />
                    更新狀態
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    {statusOptions.filter((item) => item.value).map((item) => (
                      <button
                        className={`h-10 rounded-md border px-3 text-sm font-bold transition ${
                          reservation.Status === item.value ? "border-[#315746] bg-[#315746] text-white" : "border-[#cfd8d2] bg-white text-[#315746] hover:bg-[#f0f5f2]"
                        }`}
                        disabled={saving || reservation.Status === item.value}
                        key={item.value}
                        onClick={() => handleStatusUpdate(item.value as StoreReservationStatus)}
                        type="button"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <form className="rounded-md border border-[#dfe6e1] bg-white p-4" onSubmit={handleAddNote}>
                  <div className="mb-3 flex items-center gap-2 font-extrabold text-[#203d32]">
                    <MessageSquarePlus size={18} />
                    新增備註 / 回訪
                  </div>
                  <textarea className="min-h-[112px] w-full rounded-md border border-[#cfd8d2] p-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="例如：已電話聯絡，客戶希望週五下午再確認。" value={noteBody} onChange={(event) => setNoteBody(event.target.value)} />
                  <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
                    <div>
                      <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="next-follow-up">下次回訪時間</label>
                      <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="next-follow-up" type="datetime-local" value={nextFollowUpAt} onChange={(event) => setNextFollowUpAt(event.target.value)} />
                    </div>
                    <button className="inline-flex h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={saving} type="submit">
                      {saving ? "儲存中" : "儲存備註"}
                    </button>
                  </div>
                </form>

                <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
                  <h3 className="mb-3 font-extrabold text-[#203d32]">互動紀錄</h3>
                  {interactions.length === 0 ? (
                    <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-8 text-center text-sm text-[#7b877f]">尚無互動紀錄。</div>
                  ) : (
                    <div className="space-y-3">
                      {interactions.map((item) => (
                        <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3" key={item.InteractionId}>
                          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-extrabold text-[#315746]">{item.InteractionType === "FollowUp" ? "回訪" : "備註"}</span>
                            <span className="text-xs font-bold text-[#7b877f]">{item.CreatedAt}</span>
                          </div>
                          <p className="text-sm leading-6 text-[#59665f]">{item.Body || "-"}</p>
                          {item.NextFollowUpAt && <p className="mt-2 text-xs font-bold text-[#8a6a2f]">下次回訪：{item.NextFollowUpAt}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="min-h-[420px] rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-10 text-center text-sm text-[#7b877f]">找不到預約詳情。</div>
            )}
          </section>
        </div>
      </div>
    </StoreLayout>
  );
}
